import React, { useState } from "react";
import { ICreateSalesData } from "../../../../@types";
import { formatMoney } from "../../../../utils";
import { useTranslation } from "react-i18next";
import DescriptionModal from "../../../Invoice/sections/DescriptionModal";

interface SalesCalcsProps {
  subTotal: number;
  formData: ICreateSalesData;
  calcDiscount: number;
  calcTax: number;
  calcShipping?: number;
  total: number;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isRetail: boolean;
}

const InvoiceCalculation: React.FC<SalesCalcsProps> = ({
  subTotal,
  formData,
  calcDiscount,
  calcTax,
  calcShipping,
  total,
  handleInputChange,
  isRetail,
}) => {
  const { t } = useTranslation("invoices");
   const [showDescModal, setShowDescModal] = useState(false);
    const [description, setDescription] = useState(formData.shippingDesc || "");
    const [hasDescription, setHasDescription] = useState(!!formData.shippingDesc);
    const [showShipping, setShowShipping] = useState(formData.hasShipping);
  
    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setDescription(e.target.value);
    };
  
    const saveDescription = () => {
      formData.shippingDesc = description;
      setHasDescription(!!description);
      setShowDescModal(false);
    };
  
    const handleShippingToggle = () => {
      setShowShipping(!showShipping);
      if (!showShipping) {
        // If enabling shipping, use default values
        formData.shippingCharge = formData.shippingCharge || 0;
      } else {
        // If disabling shipping, reset values
        formData.shippingCharge = 0;
      }
    };
    
    const handleShippingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      handleInputChange(e);
      // Update shipping charge when input changes
      if (e.target.name === "shippingCharge") {
        const value = parseFloat(e.target.value) || 0;
        if (value > 0) {
          setShowShipping(true);
        }
      }
    };
    
  return (
    <>
      {/* Desktop */}
      <div className="hidden lg:block md:block mt-[5em] border-t">
        <div className="flex justify-between text-[18px] ml-[-2em] mt-[1em]   pl-[1em] gap-[9em] ">
          <p className="pl-3">Sub-Total:</p>
          <p className="">{formatMoney(subTotal, formData.currency)}</p>
        </div>
        <div className="flex justify-between text-[18px] ml-[-2em] mt-[1em]  pl-[1em] gap-[9em] ">
          <div className="flex ">
            <p className="pl-3 mt-[0.2em]">{t("discount")}:</p>
            <span className="flex">
              <div className="relative w-[76px] ml-[1em] h-[40px]">
                <input
                  type="number"
                  value={formData?.discount || ""}
                  name="discount"
                  onChange={handleInputChange}
                  className="w-full h-full pr-4 border-gray-300 rounded-lg"
                />
                <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-black text-[16px] font-semibold">
                  %
                </span>
              </div>
            </span>
          </div>
          <div className="flex gap-1  pl-3 py-2 h-[40px] mt-[]">
            <p className="">
              {formatMoney(calcDiscount.toFixed(2), formData.currency)}
            </p>
          </div>
        </div>
        {/* Shipping Row for Desktop */}
              {isRetail && (
                    <>
                      <div className="flex justify-between text-[18px] py-2 mt-4">
                        <div className="flex items-center ">
                          <p className="font-medium leading-6 text-gray-600">Shipping:</p>
                          <div className="relative w-[76px] ml-[1em] h-[40px]">
                            <input
                              type="number"
                              name="shippingCharge"
                              value={formData?.shippingCharge || ""}
                              onChange={handleShippingChange}
                              className="w-full h-full pr-4 border-gray-300 rounded-lg"
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                        <div>
                          <p className=" text-lg leading--6 text-[#000000]">
                            {formatMoney(calcShipping?.toFixed(2), formData.currency)}
                          </p>
                        </div>
                      </div>
        
                      {/* Additional Description Section */}
                      {hasDescription ? (
                        <div className="pb-4">
                          <div className="flex gap-2 items-center">
                            <div className="rounded-md text-gray-700 text-sm whitespace-pre-wrap">
                              {formData.shippingDesc}
                            </div>
                            <button 
                              onClick={() => setShowDescModal(true)}
                              className="text-[#0D3C6D] text-sm font-medium"
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className=" py-2">
                          <button 
                            onClick={() => setShowDescModal(true)} 
                            className="text-[16px] text-[#0D3C6D] font-medium"
                          >
                            Additional description
                          </button>
                        </div>
                      )}
                        </>
                )}
        
        <div className="flex justify-between text-[18px] ml-[-2em] mt-[1em]  pl-[1em] gap-[9em] ">
          <div className="flex">
            <p className="pl-3 mt-[0.2em]"> VAT:</p>
            <span className="flex">
              <div className="relative w-[76px] ml-[1em] h-[40px]">
                <input
                  type="number"
                  value={formData?.tax || ""}
                  name="tax"
                  onChange={handleInputChange}
                  className="w-full h-full pr-4 border-gray-300 rounded-lg"
                />
                <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-black text-[16px] font-semibold">
                  %
                </span>
              </div>
            </span>
          </div>
          <div className="flex gap-1 pl-3 py-2  h-[40px] mt-[]">
            <p className="">
              {" "}
              {formatMoney(calcTax.toFixed(2), formData.currency)}
            </p>
          </div>
        </div>
        <div className="flex justify-between text-[18px] mt-[1em]  gap-[5em] font-semibold shadow-sm bg-[#0D3C6D] pr-[1em] py-2 text-white">
          <p className="pl-3">Total amount</p>
          <p className="">{formatMoney(total.toFixed(2), formData.currency)}</p>
        </div>
      </div>
      {/* Mobile */}
       <div className="md:hidden text-[14px] mb-8 w-full">
             <div className="flex justify-between text-[16px] py-2">
               <p className="font-medium leading-6 text-gray-600">Sub Total:</p>
               <p className="font-semibold leading-6 text-[#000000]">
                 {" "}
                 {formatMoney(subTotal?.toFixed(2), formData.currency)}
               </p>
             </div>
     
             <div className="flex items-center justify-between text-[16px] py-2">
               <div className="flex items-center gap-2">
                 <p className="">{t("discount")}</p>
                 <div className="relative w-[60px]">
                   <input
                     type="number"
                     name="discount"
                     value={formData?.discount || ""}
                     onChange={handleInputChange}
                     className="w-full py-2 font-medium text-[14px] text-gray-400 py- pr-4 bg-gray-50 border border-gray-200 rounded-[4px]"
                   />
                   <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-black font-semibold">
                     %
                   </span>
                 </div>
               </div>
               <div>
                 <p className="font-semibold text-[16px] leading--6 text-[#000000]">
                   {formatMoney(calcDiscount.toFixed(2), formData.currency)}
                 </p>
               </div>
             </div>

              {/* Shipping Row for Mobile */}
              <div className="flex justify-between text-[16px] py-2 mt-4">
                        <div className="flex items-center gap-2">
                          <p className="font-medium leading-6 ">Shipping:</p>
                          <div className="relative w-[76px] h-[40px]">
                            <input
                              type="number"
                              name="shippingCharge"
                              value={formData?.shippingCharge || ""}
                              onChange={handleShippingChange}
                              className="w-full py-2 font-medium text-[14px] text-gray-400 py- pr-4  border border-gray-200 rounded-[4px]"
                              placeholder="0.00"
                            />
                          </div>
                        </div>
                        <div>
                          <p className="font-semibold  leading--6 text-[#000000]">
                            {formatMoney(calcShipping?.toFixed(2), formData.currency)}
                          </p>
                        </div>
                      </div>
        
                      {/* Additional Description Section */}
                      {hasDescription ? (
                        <div className="px-3 pb-4">
                          <div className="flex gap-2 items-center">
                            <div className="rounded-md text-gray-700 text-sm whitespace-pre-wrap">
                              {formData.shippingDesc}
                            </div>
                            <button 
                              onClick={() => setShowDescModal(true)}
                              className="text-[#0D3C6D] text-sm font-medium"
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="px-3 py-2">
                          <button 
                            onClick={() => setShowDescModal(true)} 
                            className="text-[16px] text-[#0D3C6D] font-medium"
                          >
                            Additional description
                          </button>
                        </div>
                      )}
     
             <div className="flex items-center justify-between text-[16px] py-2">
               <div className="flex items-center gap-2">
                 <p className="">VAT</p>
                 <div className="relative w-[60px]">
                   <input
                     type="number"
                     name="tax"
                     value={formData?.tax || ""}
                     onChange={handleInputChange}
                     className="w-full py-2 font-medium text-[14px] text-gray-400 py- pr-4 bg-gray-50 border border-gray-200 rounded-[4px]"
                   />
                   <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-black font-semibold">
                     %
                   </span>
                 </div>
               </div>
               <div>
                 <p className="font-semibold text-[16px] leading--6 text-[#000000]">
                   {formatMoney(calcTax?.toFixed(2), formData.currency)}
                 </p>
               </div>
             </div>
     
             <hr className="mt-2 w-100%" />
     
             <div className="flex justify-between border-b border-gray-200 py-4 font-semibold text-[17px] text-[#000000]">
               <p className="">Total({formData.currency}):</p>
               <p className="">
                 {" "}
                 {formatMoney(total?.toFixed(2), formData.currency)}
               </p>
             </div>
           </div>
                        {/* Using the extracted Description Modal component */}
      <DescriptionModal
        show={showDescModal}
        description={description}
        onClose={() => setShowDescModal(false)}
        onSave={saveDescription}
        onChange={handleDescriptionChange}
      />
    </>
  );
};

export default InvoiceCalculation;
